import CatalogCategories from "@/pages/configuration/delay-categories";
import CatalogAdjustmentReason from "@/pages/configuration/pages/adjustment-reasons";
import CatalogAdjustmentType from "@/pages/configuration/pages/adjustment-types";
import CatalogOperator from "@/pages/configuration/pages/operators";
import ShiftReportConfig from "@/pages/configuration/pages/shift-report-config";
import TensileRequirements from "@/pages/configuration/pages/tensile-requirements";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CategoryIcon from "@mui/icons-material/Category";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import LocationIcon from "@mui/icons-material/LocationOn";
import PeopleIcon from "@mui/icons-material/People";
import StorageIcon from "@mui/icons-material/Storage";
import TimelineIcon from "@mui/icons-material/Timeline";
import {
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	ListSubheader,
} from "@mui/material";
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import CatalogDelayCodes from "../pages/configuration/delay-codes";
import CatalogLocation from "../pages/configuration/pages/locations";
import CatalogSectionFamily from "../pages/configuration/pages/section-families";
import Section from "../pages/configuration/pages/sections";
import { useAssetId } from "./hooks/useAssetId";
import {
	ConfigurationPath,
	isConfigurationAllowed,
} from "./mill-config/mill-extended-config";

export const configurationPath = "/configuration";

type SidebarButton = {
	path: ConfigurationPath;
	label: string;
	icon: ReactNode;
};

type SidebarSection = {
	name: string;
	buttons: SidebarButton[];
};

type Component = {
	path: ConfigurationPath | `${ConfigurationPath}/${string}`;
	main: () => ReactNode;
};

const sidebarSections: SidebarSection[] = [
	{
		name: "Operators",
		buttons: [{ path: "operators", label: "Operators", icon: <PeopleIcon /> }],
	},
	{
		name: "Mill Configuration",
		buttons: [
			{
				path: "section-families",
				label: "Section Families",
				icon: <StorageIcon />,
			},
			{ path: "sections", label: "Sections", icon: <StorageIcon /> },
			{ path: "locations", label: "Locations", icon: <LocationIcon /> },
			{
				path: "tensile-requirements",
				label: "Tensile Requirements",
				icon: <FitnessCenterIcon />,
			},
		],
	},
	{
		name: "Threshold",
		buttons: [
			{
				path: "shift-report-config",
				label: "Threshold Adjustments",
				icon: <AssessmentIcon />,
			},
		],
	},
	{
		name: "Delays",
		buttons: [
			{ path: "delay-categories", label: "Categories", icon: <CategoryIcon /> },
			{ path: "delay-codes", label: "Codes", icon: <TimelineIcon /> },
		],
	},
	{
		name: "Adjustments",
		buttons: [
			{ path: "adjustments-types", label: "Types", icon: <CategoryIcon /> },
			{ path: "adjustments-reasons", label: "Reasons", icon: <StorageIcon /> },
		],
	},
];

const routeComponents: Component[] = [
	{ path: "delay-categories", main: () => <CatalogCategories /> },
	{ path: "delay-codes", main: () => <CatalogDelayCodes /> },
	{ path: "locations", main: () => <CatalogLocation /> },
	{ path: "sections", main: () => <Section /> },
	{ path: "sections/:sectionId", main: () => <Section /> },
	{ path: "section-families", main: () => <CatalogSectionFamily /> },
	{ path: "operators", main: () => <CatalogOperator /> },
	{ path: "operators/:operatorId", main: () => <CatalogOperator /> },
	{ path: "adjustments-types", main: () => <CatalogAdjustmentType /> },
	{ path: "adjustments-types/:typeId", main: () => <CatalogAdjustmentType /> },
	{ path: "adjustments-reasons", main: () => <CatalogAdjustmentReason /> },
	{
		path: "adjustments-reasons/:reasonId",
		main: () => <CatalogAdjustmentReason />,
	},
	{ path: "tensile-requirements", main: () => <TensileRequirements /> },
	{ path: "shift-report-config", main: () => <ShiftReportConfig /> },
];

const components = routeComponents.map((route) => ({
	path: route.path,
	main: route.main,
}));

const sidebar = ({ option }: { option?: string }) => {
	const assetId = useAssetId();

	return (
		<>
			{sidebarSections.map((section) => {
				const visibleButtons = section.buttons.filter((button) =>
					isConfigurationAllowed(button.path, assetId),
				);

				if (visibleButtons.length === 0) return null;

				return (
					<List
						key={section.name}
						subheader={
							<ListSubheader
								sx={{
									backgroundColor: (theme) => theme.palette.background.paper,
									color: (theme) => theme.palette.primary.main,
									fontWeight: 600,
								}}
							>
								{section.name}
							</ListSubheader>
						}
					>
						{visibleButtons.map((button) => (
							<ListItemButton
								key={button.path}
								selected={option === button.path}
								component={Link}
								to={`${configurationPath}/${button.path}`}
							>
								<ListItemIcon>{button.icon}</ListItemIcon>
								<ListItemText primary={button.label} />
							</ListItemButton>
						))}
					</List>
				);
			})}
		</>
	);
};

const customConfigurations: any = [components, sidebar];

export { customConfigurations };
